import React from 'react';
import { Container, Box, Typography, Paper, Divider, List, ListItem, ListItemText, Link, Chip, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { APP_NAME } from '../../config/branding';

export default function Privacy() {
  const { t } = useTranslation();
  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
      <Stack spacing={3} sx={{ mb: 4 }}>
        <Chip color="primary" label={t('legal_privacy.chip')} sx={{ alignSelf: 'flex-start' }} />
        <Typography variant="h3" component="h1">
          {t('legal_privacy.title', { appName: APP_NAME })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('legal_privacy.last_updated')}: {new Date().toLocaleDateString()}
        </Typography>
      </Stack>

      <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 2, bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { md: '320px 1fr' }, gap: { xs: 2, md: 4 } }}>
          <Box sx={{ position: { md: 'sticky' }, top: { md: 88 }, alignSelf: 'start' }}>
            <Typography variant="overline" color="text.secondary">{t('legal_privacy.summary')}</Typography>
            <List dense>
              <ListItem component="a" href="#intro" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_privacy.sections.intro')} />
              </ListItem>
              <ListItem component="a" href="#donnees" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_privacy.sections.data')} />
              </ListItem>
              <ListItem component="a" href="#finalites" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_privacy.sections.purposes')} />
              </ListItem>
              <ListItem component="a" href="#conservation" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_privacy.sections.retention')} />
              </ListItem>
              <ListItem component="a" href="#partage" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_privacy.sections.sharing')} />
              </ListItem>
              <ListItem component="a" href="#transferts" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_privacy.sections.transfers')} />
              </ListItem>
              <ListItem component="a" href="#securite" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_privacy.sections.security')} />
              </ListItem>
              <ListItem component="a" href="#droits" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_privacy.sections.rights')} />
              </ListItem>
              <ListItem component="a" href="#cookies" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_privacy.sections.cookies')} />
              </ListItem>
              <ListItem component="a" href="#enfants" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_privacy.sections.minors')} />
              </ListItem>
              <ListItem component="a" href="#maj" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_privacy.sections.updates')} />
              </ListItem>
              <ListItem component="a" href="#contact" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_privacy.sections.contact')} />
              </ListItem>
              <ListItem component="a" href="#responsable" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_privacy.sections.controller')} />
              </ListItem>
              <ListItem component="a" href="#dpo" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_privacy.sections.dpo')} />
              </ListItem>
              <ListItem component="a" href="#sous-traitants" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_privacy.sections.processors')} />
              </ListItem>
              <ListItem component="a" href="#incidents" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_privacy.sections.incidents')} />
              </ListItem>
              <ListItem component="a" href="#marketing" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_privacy.sections.marketing')} />
              </ListItem>
              <ListItem component="a" href="#cookies-details" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_privacy.sections.cookies_detail')} />
              </ListItem>
            </List>
          </Box>

          <Box>
            <Box id="intro" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_privacy.content.intro_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_privacy.content.intro_text', { appName: APP_NAME })}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="donnees" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_privacy.content.data_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_privacy.content.data_intro')}
              </Typography>
              <List sx={{ pl: 2 }}>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_privacy.content.data_1')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_privacy.content.data_2')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_privacy.content.data_3')} />
                </ListItem>
              </List>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="finalites" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_privacy.content.purposes_title')}</Typography>
              <List sx={{ pl: 2 }}>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_privacy.content.purposes_1')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_privacy.content.purposes_2')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_privacy.content.purposes_3')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_privacy.content.purposes_4')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_privacy.content.purposes_5')} />
                </ListItem>
              </List>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="conservation" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_privacy.content.retention_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_privacy.content.retention_text')}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="partage" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_privacy.content.sharing_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_privacy.content.sharing_text', { appName: APP_NAME })}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="transferts" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_privacy.content.transfers_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_privacy.content.transfers_text')}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="securite" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_privacy.content.security_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_privacy.content.security_text')}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="droits" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_privacy.content.rights_title')}</Typography>
              <List sx={{ pl: 2 }}>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_privacy.content.rights_1')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_privacy.content.rights_2')} />
                </ListItem>
              </List>
              <Typography variant="body1" paragraph>
                {t('legal_privacy.content.rights_contact')} <Link href="mailto:contact@saloone.fr">contact@saloone.fr</Link>.
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="cookies" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_privacy.content.cookies_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_privacy.content.cookies_text')}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="enfants" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_privacy.content.minors_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_privacy.content.minors_text', { appName: APP_NAME })}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="maj" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_privacy.content.updates_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_privacy.content.updates_text')}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="contact">
              <Typography variant="h5" gutterBottom>{t('legal_privacy.content.contact_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_privacy.content.contact_text')} <Link href="mailto:contact@saloone.fr">contact@saloone.fr</Link>.
              </Typography>
            </Box>            <Divider sx={{ my: 3 }} />
            <Box id="responsable" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_privacy.content.controller_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_privacy.content.controller_text', { appName: APP_NAME })}
              </Typography>
            </Box>
            <Divider sx={{ my: 3 }} />
            <Box id="dpo" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_privacy.content.dpo_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_privacy.content.dpo_text')}
              </Typography>
            </Box>
            <Divider sx={{ my: 3 }} />
            <Box id="sous-traitants" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_privacy.content.processors_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_privacy.content.processors_text')}
              </Typography>
            </Box>
            <Divider sx={{ my: 3 }} />
            <Box id="incidents" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_privacy.content.incidents_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_privacy.content.incidents_text')}
              </Typography>
            </Box>
            <Divider sx={{ my: 3 }} />
            <Box id="marketing" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_privacy.content.marketing_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_privacy.content.marketing_text')}
              </Typography>
            </Box>
            <Divider sx={{ my: 3 }} />
            <Box id="cookies-details" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_privacy.content.cookies_detail_title')}</Typography>
              <List sx={{ pl: 2 }}>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_privacy.content.cookies_detail_1')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_privacy.content.cookies_detail_2')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_privacy.content.cookies_detail_3')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_privacy.content.cookies_detail_4')} />
                </ListItem>
              </List>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
}





